import React, { useState, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Folder,
  Clock,
  Eye,
  Star,
  Tag,
  Trash2,
  Mail,
  Users,
  Shield,
  Bell
} from 'lucide-react';

const DataroomSidebar = memo(({ dataroomUuid, isOwner }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine initial active item from location
  const getInitialActiveItem = () => {
    if (location.pathname.includes('/files/recycle-bin')) return 'recycle-bin';
    if (location.pathname.includes('/files/favorites')) return 'favorites';
    if (location.pathname.includes('/files/recent-uploaded')) return 'recently-uploaded';
    if (location.pathname.includes('/files/recent-viewed')) return 'recently-viewed';
    if (location.pathname.includes('/files/tags')) return 'tags';
    if (location.pathname.includes('/files/invitations')) return 'invitations';
    if (location.pathname.includes('/files/members')) return 'members';
    if (location.pathname.includes('/files/roles')) return 'roles';
    if (location.pathname.includes('/files/email-notifications')) return 'email-notifications';

    return 'file-manager';
  };

  const [activeItem, setActiveItem] = useState(getInitialActiveItem());

  // Update active item when location changes
  React.useEffect(() => {
    if (location.pathname.includes('/files/recycle-bin')) {
      setActiveItem('recycle-bin');
    } else if (location.pathname.includes('/files/favorites')) {
      setActiveItem('favorites');
    } else if (location.pathname.includes('/files/recent-uploaded')) {
      setActiveItem('recently-uploaded');
    } else if (location.pathname.includes('/files/recent-viewed')) {
      setActiveItem('recently-viewed');
    } else if (location.pathname.includes('/files/tags')) {
      setActiveItem('tags');
    } else if (location.pathname.includes('/files/invitations')) {
      setActiveItem('invitations');
    } else if (location.pathname.includes('/files/members')) {
      setActiveItem('members');
    } else if (location.pathname.includes('/files/roles')) {
      setActiveItem('roles');
    } else if (location.pathname.includes('/files/email-notifications')) {
      setActiveItem('email-notifications');
    } else if (location.pathname.endsWith('/files')) {
      // For base /files route, set to file-manager
      setActiveItem('file-manager');
    }
  }, [location.pathname, location.search]);

  const allMenuItems = [
    {
      id: 'dashboard',
      icon: Home,
      label: 'Home - All Files',
      action: () => {
        // Show the sidebar when going back to Dataroom List
        if (window.__setSidebarOpen) {
          window.__setSidebarOpen(true);
        }
        navigate('/datarooms');
      },
      color: 'text-gray-700',
      hoverColor: 'hover:text-gray-900'
    },
    {
      id: 'file-manager',
      icon: Folder,
      label: 'File Manager',
      action: () => {
        if (dataroomUuid) {
          navigate(`/datarooms/${dataroomUuid}/files`);
        }
      },
      color: 'text-blue-600',
      hoverColor: 'hover:text-blue-700'
    },
    {
      id: 'recently-uploaded',
      icon: Clock,
      label: 'Recently Uploaded',
      action: () => {
        if (dataroomUuid) {
          navigate(`/datarooms/${dataroomUuid}/files/recent-uploaded`);
        }
      },
      color: 'text-purple-600',
      hoverColor: 'hover:text-purple-700'
    },
    {
      id: 'recently-viewed',
      icon: Eye,
      label: 'Recently Viewed',
      action: () => {
        if (dataroomUuid) {
          navigate(`/datarooms/${dataroomUuid}/files/recent-viewed`);
        }
      },
      color: 'text-green-600',
      hoverColor: 'hover:text-green-700'
    },
    {
      id: 'favorites',
      icon: Star,
      label: 'Favorite Files',
      action: () => {
        if (dataroomUuid) {
          navigate(`/datarooms/${dataroomUuid}/files/favorites`);
        }
      },
      color: 'text-amber-500',
      hoverColor: 'hover:text-amber-600'
    },
    {
      id: 'tags',
      icon: Tag,
      label: 'Tags',
      action: () => {
        if (dataroomUuid) {
          navigate(`/datarooms/${dataroomUuid}/files/tags`);
        }
      },
      color: 'text-indigo-600',
      hoverColor: 'hover:text-indigo-700'
    },
    ...(isOwner ? [
      {
        id: 'invitations',
        icon: Mail,
        label: 'Invitations',
        action: () => {
          if (dataroomUuid) {
            navigate(`/datarooms/${dataroomUuid}/files/invitations`);
          }
        },
        color: 'text-pink-600',
        hoverColor: 'hover:text-pink-700'
      },
      {
        id: 'members',
        icon: Users,
        label: 'Members',
        action: () => {
          if (dataroomUuid) {
            navigate(`/datarooms/${dataroomUuid}/files/members`);
          }
        },
        color: 'text-cyan-600',
        hoverColor: 'hover:text-cyan-700'
      },
      {
        id: 'roles',
        icon: Shield,
        label: 'Roles',
        action: () => {
          if (dataroomUuid) {
            navigate(`/datarooms/${dataroomUuid}/files/roles`);
          }
        },
        color: 'text-violet-600',
        hoverColor: 'hover:text-violet-700'
      },
      {
        id: 'email-notifications',
        icon: Bell,
        label: 'Email Notifications',
        action: () => {
          if (dataroomUuid) {
            navigate(`/datarooms/${dataroomUuid}/files/email-notifications`);
          }
        },
        color: 'text-orange-600',
        hoverColor: 'hover:text-orange-700'
      }
    ] : [])
  ];

  const menuItems = allMenuItems;

  const handleItemClick = (item) => {
    // For Home and File Manager, both show all files, so we can activate either
    if (item.id === 'dashboard' || item.id === 'file-manager') {
      setActiveItem(item.id);
    } else {
      setActiveItem(item.id);
    }
    if (item.action) {
      item.action();
    }
  };

  return (
    <div className="w-16 bg-white border-r border-gray-100 flex flex-col h-full min-h-0">
      {/* Main Menu Items */}
      <div className="flex-1 py-3 min-h-0 overflow-y-auto">
        <nav className="space-y-0.5">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = activeItem === item.id;

            return (
              <div
                key={item.id}
                className="relative"
              >
                {active && (
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-0.5 h-6 bg-blue-500 rounded-r" />
                )}
                <button
                  onClick={() => handleItemClick(item)}
                  className={`
                    w-full py-2.5 flex items-center justify-center transition-all relative
                    ${active
                      ? 'bg-blue-50'
                      : 'hover:bg-gray-50'
                    }
                  `}
                  title={item.label}
                >
                  <Icon
                    size={18}
                    strokeWidth={active ? 2.5 : 1.5}
                    className={`
                      transition-all
                      ${active
                        ? 'text-blue-600'
                        : `${item.color} ${item.hoverColor}`
                      }
                    `}
                  />
                </button>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Recycle Bin at Bottom - Fixed Position */}
      <div className="border-t border-gray-100 bg-white">
        <div className="relative">
          {activeItem === 'recycle-bin' && (
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-0.5 h-6 bg-red-500 rounded-r" />
          )}
          <button
            onClick={() => {
              setActiveItem('recycle-bin');
              if (dataroomUuid) {
                navigate(`/datarooms/${dataroomUuid}/files/recycle-bin`);
              }
            }}
            className={`
              w-full py-3 flex items-center justify-center transition-all
              ${activeItem === 'recycle-bin'
                ? 'bg-red-50'
                : 'hover:bg-gray-50'
              }
            `}
            title="Recycle Bin"
          >
            <Trash2
              size={18}
              strokeWidth={activeItem === 'recycle-bin' ? 2.5 : 1.5}
              className={`
                transition-all
                ${activeItem === 'recycle-bin'
                  ? 'text-red-600'
                  : 'text-gray-600 hover:text-gray-800'
                }
              `}
            />
          </button>
        </div>
      </div>
    </div>
  );
});

DataroomSidebar.displayName = 'DataroomSidebar';

export default DataroomSidebar;